import { useEffect, useState } from "react";

/**
 * モデルはデータ更新の粒度ごとにこのクラスをextendsまたはメンバとして持つこと。
 * データ変更完了のタイミングでchanged()を呼ぶこと。
 */
class Model {
  private notify: (n: any) => void = (n: any) => {};
  public changed(): void {
    this.notify(new (class {})());
  }
  public setNotifier(n: any): void {
    this.notify = n;
  }
  public resetNotifier(): void {
    this.notify = (n: any) => {};
  }
}

/**
 * ステートを管理するモデルuseModelと一緒に使う。
 * 典型的な使い方は
 * class Model extends StateModel<'init' | 'loading' | 'complete'>
 * {
 *  public constructor()
 *  {
 *    super('init')
 *  }
 * ...
 * }
 */
class StateModel<StateType> extends Model {
  private state: StateType;
  /**
   * コンストラクタ
   * @param initialState 初期ステート
   * @param bRedrawNow 今すぐ再描画するかどうか。デフォルト値はfalse
   */
  public constructor(initialState: StateType, bRedrawNow = false) {
    super();
    this.state = initialState;
    if (bRedrawNow) {
      super.changed();
    }
  }
  /**
   * ステート変更
   * @param state ステート
   * @param bRedrawNow 今すぐ再描画するかどうか。デフォルト値はtrue
   */
  public setState(state: StateType, bRedrawNow = true): void {
    if (this.state !== state) {
      this.state = state;
      if (bRedrawNow) {
        super.changed();
      }
    }
  }
  /**
   * 現在のステートを返す
   * @returns ステート
   */
  public getState(): StateType {
    return this.state;
  }
}

/**
 * 更新時に画面状態を復旧させる
 */
class ScreenStateModel<StateType> extends StateModel<StateType> {
  public constructor(initialState: StateType, bRedrawNow = false) {
    let state = sessionStorage["CurrentPage"];
    if (
      !state ||
      state === "EditLockLook" ||
      state === "EditKaiteki" ||
      state === "AddLockLook"
    ) {
      // リロード復帰未サポートの画面
      // Admin画面に適用する場合は要修正
      state = null;
      sessionStorage["CurrentPage"] = "Console";
    }
    super(state ?? initialState, bRedrawNow);
  }
  /**
   * ステートを変更し、セッションストレージに保存
   * @param state ステート
   * @param bRedrawNow 今すぐ再描画するかどうか。デフォルト値はtrue
   */
  public setState(state: StateType, bRedrawNow = true): void {
    super.setState(state, bRedrawNow);
    sessionStorage["CurrentPage"] = state;
  }
}

/**
 * データが変更された際にuseModelを呼び出したコンポーネントを再描画させる
 * @param data 紐付けるデータ
 */
function useModel(data: Model): void {
  const [, set] = useState(null);
  data.setNotifier(set);
  // ページ破棄時に自動リセット
  useEffect(() => {
    return () => {
      data.resetNotifier();
    };
  }, [data]);
}

export type Signal = () => void;

/**
 * 呼び出したコンポーネントを更新する関数を返す。
 * フォームのデータが更新された際などに利用。
 * (フォームのデータはモデルから取得すること)
 * @returns Signal
 */
function useSignal(): Signal {
  const [, set] = useState(new (class {})());
  return () => {
    set(new (class {})());
  };
}

export { Model, StateModel, ScreenStateModel, useModel, useSignal };
